import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { useWorkspaceIdFromCurrentUrl } from '@/services/router/use-workspace-id';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { setMailServerCache } from './cache';
import { mailServerKeys, parseMailServer, type MailServer } from './entity';

/**
 * Mail-server reads. `staleTime: 0` because every screen that reads the list
 * also writes it (add / edit / delete a server, change which one is default)
 * and must never render the pre-mutation collection.
 */

const STALE_TIME = 0;

/** @public */
export async function getMailServers(workspaceId: number, qc: QueryClient): Promise<MailServer[]> {
  const data = await hydraUiApi.mailservers.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const parsed = data.mail_servers.map(parseMailServer);
  // Sideload each row into its detail slot so an edit screen resolves from
  // cache without an extra GET.
  for (const server of parsed) {
    setMailServerCache(qc, workspaceId, server);
  }
  return parsed;
}

/** @public */
export const getMailServersQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    // `TListParams = void` for this entity — the no-filters list slot.
    queryKey: mailServerKeys.list(workspaceId, undefined),
    queryFn: () => getMailServers(workspaceId, qc),
    staleTime: STALE_TIME,
  });

interface UseMailServersOptions {
  queryConfig?: QueryConfig<typeof getMailServersQueryOptions>;
}

/** @public */
export const useMailServers = ({ queryConfig }: UseMailServersOptions = {}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getMailServersQueryOptions(workspaceId, qc),
    ...queryConfig,
  });
};

interface UseMailServersOptionalOptions {
  /** The workspace to read. When not set, the hook uses the workspace in the URL. */
  workspaceId?: number;
  enabled?: boolean;
}

/**
 * The same read, non-suspending — for screens where the server list annotates
 * the page rather than forming it, and so must not hold up (or fail with) what
 * it annotates. `data` is `undefined` while the read is in flight, after it
 * fails, and when there is no workspace id, so none of these can be read as
 * "this workspace has no custom SMTP".
 *
 * Layout chrome renders on routes without a numeric id (e.g.
 * `/workspaces/last/...`), so it passes `workspaceId` explicitly. With no valid
 * id the query is skipped instead of thrown.
 *
 * @public
 */
export const useMailServersOptional = ({ workspaceId, enabled }: UseMailServersOptionalOptions = {}) => {
  const urlWorkspaceId = useWorkspaceIdFromCurrentUrl();
  const qc = useQueryClient();

  let validWorkspaceId: number | null = null;
  if (workspaceId != null && workspaceId > 0) {
    validWorkspaceId = workspaceId;
  } else if (urlWorkspaceId != null && urlWorkspaceId > 0) {
    validWorkspaceId = urlWorkspaceId;
  }

  // With no id, the query goes to a sentinel key outside the workspace
  // namespace, and `skipToken` makes sure no data lands on it.
  return useQuery({
    queryKey:
      validWorkspaceId != null ? mailServerKeys.list(validWorkspaceId, undefined) : ['mail-server', 'no-workspace'],
    queryFn: validWorkspaceId != null ? () => getMailServers(validWorkspaceId, qc) : skipToken,
    staleTime: STALE_TIME,
    enabled,
  });
};
